import { createPrng } from '@play-co/replicant';
import { DateTime } from 'luxon';
import type { State } from '../State';
import { getCurrentPetState } from '../fate/fate.getters';
import { getPromoShownCounter, isPromoReady } from '../promo/promo.getters';
import features from '../ruleset/features';
import { shuffleArray } from '../utils/random';
import {
  dreamDriftHeroes,
  dreamDriftInstanceDuration,
  dreamDriftLevels,
  dreamDriftNodeBands,
  dreamDriftMilestones,
  dreamDriftSchedule,
  dreamDriftScheduleInitDate,
  type DreamDriftTheme,
  dreamDriftThemes,
  dreamDriftThemeRotationStart,
  dreamDriftUnlockLevel,
} from './dreamDrift.ruleset';

/**
 * Check whether Dream Drift is currently available to the player.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether the feature is enabled, unlocked, and inside an event window.
 */
export function dreamDriftIsAvailable(state: State, now: number): boolean {
  if (!features(state).dreamDrift) return false;

  // Players below the unlock level cannot access Dream Drift.
  if (state.match2.level < dreamDriftUnlockLevel) return false;

  // Availability requires an active schedule event.
  return !!dreamDriftSchedule.getCurrentEvent(now);
}

/**
 * Check whether Dream Drift should show its lifetime information FTUE.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether Dream Drift is available and has never been seen before.
 */
export function dreamDriftShouldAutoOpenFirstTime(
  state: State,
  now: number,
): boolean {
  // Lifetime discovery requires the current event promo to be eligible.
  if (!dreamDriftShouldAutoOpenCurrentEvent(state, now)) return false;

  // Any previous Dream Drift promo view permanently satisfies the lifetime FTUE.
  return getPromoShownCounter(state, 'dreamDriftEventPromo') === 0;
}

/**
 * Get the active scheduled event for an available player.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns The current schedule event, or undefined when unavailable.
 */
export function dreamDriftGetCurrentEvent(state: State, now: number) {
  // Unavailable players do not have a current Dream Drift event.
  if (!dreamDriftIsAvailable(state, now)) return undefined;

  // All availability checks passed, return the current scheduled window.
  return dreamDriftSchedule.getCurrentEvent(now);
}

/**
 * Check whether Dream Drift should automatically open for the current event.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether the current event has not been seen yet.
 */
export function dreamDriftShouldAutoOpenCurrentEvent(
  state: State,
  now: number,
): boolean {
  // Automatic entry requires a currently available Dream Drift event.
  if (!dreamDriftIsAvailable(state, now)) return false;

  // The shared promo cooldown suppresses repeat entry within the three-day event.
  return isPromoReady(state, 'dreamDriftEventPromo', now);
}

/**
 * Check whether the player started an instance during the current event window.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether the stored instance belongs to the current available event.
 */
export function dreamDriftHasStartedCurrentEvent(
  state: State,
  now: number,
): boolean {
  // The comparison requires a currently available event and stored instance.
  const currentEvent = dreamDriftGetCurrentEvent(state, now);
  const instance = state.dreamDrift?.instance;
  if (!currentEvent || !instance) return false;

  // Resolve the schedule window containing the instance's start timestamp.
  const instanceEvent = dreamDriftSchedule.getCurrentEvent(instance.startedAt);

  // Matching schedule identities prove the instance belongs to this event.
  return instanceEvent?.id === currentEvent.id;
}

/**
 * Get the Dream Drift event week for a timestamp.
 *
 * @param now Timestamp whose event week should be calculated.
 * @returns One for the first event week, or zero before it begins.
 */
export function dreamDriftGetEventWeek(now: number): number {
  // Interpret the initialization boundary in the configured event timezone.
  const initDate = DateTime.fromISO(dreamDriftScheduleInitDate, {
    zone: 'Asia/Tokyo',
  });

  // Interpret the supplied timestamp in the same timezone before comparing it.
  const currentDate = DateTime.fromMillis(now, {
    zone: 'Asia/Tokyo',
  });

  // Count complete weekly intervals from initialization to the supplied time.
  const eventWeek = Math.floor(currentDate.diff(initDate, 'weeks').weeks);

  // Dates before the first event week remain in preparatory week zero.
  return Math.max(0, eventWeek);
}

/**
 * Get the Dream Drift theme assigned to a timestamp's event week.
 *
 * @param now Timestamp whose event theme should be calculated.
 * @returns Theme assigned by the ordered weekly rotation.
 */
export function dreamDriftGetEventTheme(now: number): DreamDriftTheme {
  // Anchor artwork rotation independently of the existing event and level schedule.
  const rotationStart = DateTime.fromISO(dreamDriftThemeRotationStart, {
    zone: 'Asia/Tokyo',
  });
  const currentDate = DateTime.fromMillis(now, { zone: 'Asia/Tokyo' });
  const elapsedWeeks = Math.floor(
    currentDate.diff(rotationStart, 'weeks').weeks,
  );

  // Earlier events use the first theme; each Friday advances through the rotation.
  const themeIndex = Math.max(0, elapsedWeeks) % dreamDriftThemes.length;

  // Cycle through the configured themes in their declared order.
  return dreamDriftThemes[themeIndex];
}

/**
 * Get the current pet state when it prevents Dream Drift play.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Current unavailable pet state, or undefined when the pet is available.
 */
export function dreamDriftIsPetUnavailable(state: State, now: number) {
  // Resolve the pet's true current state from server-authoritative time.
  const currentPetState = getCurrentPetState(state, now);

  // Return the current unavailable state, or undefined when available.
  return (['dead', 'dying', 'sick', 'asleep', 'daycare'] as const).find(
    (petState) => petState === currentPetState,
  );
}

/**
 * Get the remaining duration of the current Dream Drift event.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Remaining event milliseconds, or zero when Dream Drift is unavailable.
 */
export function dreamDriftGetEventRemainingTime(
  state: State,
  now: number,
): number {
  // Unavailable players do not have an active Dream Drift event countdown.
  const currentEvent = dreamDriftGetCurrentEvent(state, now);
  if (!currentEvent) return 0;

  // Return the remaining event duration without allowing a negative timer.
  return Math.max(0, currentEvent.end - now);
}

/**
 * Check whether the retained Dream Drift instance has rewards to claim.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether completed or expired-instance rewards remain available.
 */
export function dreamDriftHasRewardsAvailable(
  state: State,
  now: number,
): boolean {
  if (!features(state).dreamDrift) return false;

  // Missing and already claimed instances have no rewards available.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.claimed) return false;

  // Rewards require an instance created inside a configured event window.
  const instanceEvent = dreamDriftSchedule.getCurrentEvent(instance.startedAt);
  if (!instanceEvent) return false;

  // A later event ignores the retained instance until a new run overwrites it.
  const nextEvent = dreamDriftSchedule.getNextEvent(instanceEvent.end);
  if (nextEvent && now >= nextEvent.start) return false;

  // Completion makes the combined milestone reward available immediately.
  if (instance.result === 'completed') return true;

  // Incomplete progress only earns its milestone after the event has ended.
  const instanceHasExpired =
    instance.result === 'expired' ||
    dreamDriftGetInstanceRemainingTime(state, now) === 0;
  if (!instanceHasExpired || now < instanceEvent.end) return false;

  // Expired instances qualify upon reaching a non-final milestone level.
  const maxLevelReached = Math.max(
    instance.maxLevelReached ?? 0,
    instance.progress,
  );
  return dreamDriftGetMilestones()
    .slice(0, -1)
    .some(({ level }) => level <= maxLevelReached + 1);
}

/**
 * Get the remaining Dream Meter duration for the active instance.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Remaining milliseconds, clamped to zero.
 */
export function dreamDriftGetInstanceRemainingTime(
  state: State,
  now: number,
): number {
  // Missing or stored terminal instances have no remaining Dream Meter time.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.result) return 0;

  // Instances without a valid source event have no remaining time.
  const event = dreamDriftSchedule.getCurrentEvent(instance.startedAt);
  if (!event) return 0;

  // The event window truncates an instance that would otherwise overrun it.
  const deadline = Math.min(
    instance.startedAt + dreamDriftInstanceDuration,
    event.end,
  );

  // Return the remaining duration without allowing a negative timer.
  return Math.max(0, deadline - now);
}

/**
 * Check whether the player currently has an active Dream Drift instance.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether Dream Drift is enabled and the instance has Dream Meter time remaining.
 */
export function dreamDriftIsInstanceActive(state: State, now: number): boolean {
  // Disabled players cannot have an active Dream Drift instance.
  if (!features(state).dreamDrift) return false;

  // An instance is active only while its server-authoritative timer remains positive.
  return dreamDriftGetInstanceRemainingTime(state, now) > 0;
}

/**
 * Get the remaining time displayed by the Dream Drift map timer.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Active instance time, active event time, or zero when ended.
 */
export function dreamDriftGetMapRemainingTime(
  state: State,
  now: number,
): number {
  // A valid ongoing instance takes priority over the broader event window.
  const instanceRemainingTime = dreamDriftGetInstanceRemainingTime(state, now);
  if (instanceRemainingTime > 0) return instanceRemainingTime;

  // Players without an ongoing instance count down to the current event end.
  return dreamDriftGetEventRemainingTime(state, now);
}

/**
 * Get the instance eligible for event or between-event reward presentation.
 *
 * @param state Player state containing the retained instance.
 * @param now Server-authoritative current timestamp.
 * @returns The retained instance until the next event starts, otherwise undefined.
 */
export function dreamDriftGetPresentationInstance(state: State, now: number) {
  // Instances must originate in a configured event that has already started.
  const instance = state.dreamDrift?.instance;
  if (!instance || now < instance.startedAt) return undefined;
  const instanceEvent = dreamDriftSchedule.getCurrentEvent(instance.startedAt);
  if (!instanceEvent) return undefined;

  // Preserve the reward-claim gap, but never carry presentation into a later event.
  const nextEvent = dreamDriftSchedule.getNextEvent(instanceEvent.end);
  if (nextEvent && now >= nextEvent.start) return undefined;

  // Keep the original result and rewards in state without exposing stale runs.
  return instance;
}

/**
 * Get the Dream Drift progress displayed on the progression map.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Stored progress for a valid ongoing or completed instance, otherwise zero.
 */
export function dreamDriftGetMapProgress(state: State, now: number): number {
  // New events ignore retained progress from previous event instances.
  const instance = dreamDriftGetPresentationInstance(state, now);
  if (!instance) return 0;

  // Valid completed instances keep their final stored map progress.
  if (instance.result === 'completed') return instance.progress;

  // Expired terminal instances reset the map to its first node.
  if (instance.result) return 0;

  // Timed-out unfinished instances no longer expose their stored progress.
  if (dreamDriftGetInstanceRemainingTime(state, now) === 0) return 0;

  // The valid ongoing instance exposes its highest cleared progress.
  return instance.progress;
}

/** Values required to present and acknowledge one Dream Drift map transition. */
export type DreamDriftMapPresentation = {
  /** Identifier of the instance whose visual state is being presented. */
  startedAt?: number;
  /** Map position from which the avatar should begin. */
  fromProgress: number;
  /** Current authoritative map position at which the avatar should finish. */
  toProgress: number;
  /** Persisted source that must be acknowledged after presentation. */
  pendingProgress?: number;
};

/**
 * Get the pending visual transition for the Dream Drift progression map.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Source, destination, and acknowledgement values for the map.
 */
export function dreamDriftGetMapPresentation(
  state: State,
  now: number,
): DreamDriftMapPresentation {
  // Derive the destination exclusively from the authoritative instance state.
  const toProgress = dreamDriftGetMapProgress(state, now);

  // New events display the first node without acknowledging an old transition.
  const instance = dreamDriftGetPresentationInstance(state, now);
  if (!instance) {
    return {
      fromProgress: toProgress,
      toProgress,
    };
  }

  // Pending visual state takes priority over the current destination.
  const pendingProgress = instance.pendingProgress;

  // Return a static presentation when no transition remains unseen.
  return {
    startedAt: instance.startedAt,
    fromProgress: pendingProgress ?? toProgress,
    toProgress,
    pendingProgress,
  };
}

/**
 * Check whether a Dream Drift puzzle attempt is currently playing.
 *
 * @param state Player state to inspect.
 * @returns Whether the active instance has a playing progression node.
 */
export function dreamDriftGetPuzzlePlaying(state: State): boolean {
  // Positive node values identify an active Dream Drift puzzle attempt.
  return (state.dreamDrift?.instance?.playing ?? 0) > 0;
}

/**
 * Check whether the player completed Dream Drift during the current event.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether the stored completion belongs to the active event window.
 */
export function dreamDriftHasCompletedCurrentEvent(
  state: State,
  now: number,
): boolean {
  // A completion can only belong to a currently available event.
  const currentEvent = dreamDriftGetCurrentEvent(state, now);
  if (!currentEvent) return false;

  // Only completed instances can close the current event for the player.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.result !== 'completed') return false;

  // Match the stored instance to the active event so past completions do not block new weeks.
  const instanceEvent = dreamDriftSchedule.getCurrentEvent(instance.startedAt);
  return instanceEvent?.id === currentEvent.id;
}

/**
 * Check whether the player can begin a new Dream Drift instance.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether all event, pet, and lifecycle gates pass.
 */
export function dreamDriftCanStartInstance(state: State, now: number): boolean {
  // A new instance requires a currently available event.
  const currentEvent = dreamDriftGetCurrentEvent(state, now);
  if (!currentEvent) return false;

  // An unavailable pet state must be resolved before starting.
  if (dreamDriftIsPetUnavailable(state, now)) return false;

  // Players without an existing instance can start immediately.
  const instance = state.dreamDrift?.instance;
  if (!instance) return true;

  // The event can only be completed once per window.
  if (dreamDriftHasCompletedCurrentEvent(state, now)) return false;

  // An ongoing instance must be resumed instead of replaced.
  const instanceIsOngoing =
    !instance.result && dreamDriftGetInstanceRemainingTime(state, now) > 0;
  if (instanceIsOngoing) return false;

  // All start gates passed.
  return true;
}

/**
 * Get the progression nodes that act as loss-recovery checkpoints.
 *
 * @returns Ordered checkpoint node values derived from non-final milestones.
 */
export function dreamDriftGetCheckpoints(): number[] {
  // Every milestone except the final completion milestone is a checkpoint.
  return dreamDriftGetMilestones()
    .slice(0, -1)
    .map(({ level }) => level);
}

/**
 * Get the final progression node that completes a Dream Drift instance.
 *
 * @returns Node value of the final configured milestone.
 */
export function dreamDriftGetFinalNode(): number {
  // The final ordered milestone defines the end of the progression path.
  return dreamDriftGetMilestones().at(-1)!.level;
}

/**
 * Get the milestones for the shared twelve-node Dream Drift path.
 *
 * @returns The ordered rewards and checkpoints for the twelve-node path.
 */
export function dreamDriftGetMilestones() {
  // Every player follows the twelve-node path, including past experiment buckets.
  return dreamDriftMilestones;
}

/**
 * Get the checkpoint level restored after losing a node.
 *
 * @param node Progression node that was being played.
 * @returns Checkpoint level for the attempted node, or zero before level four.
 */
export function dreamDriftGetCheckpoint(state: State, node: number): number {
  // Track the latest checkpoint at or before the attempted node.
  let checkpoint = 0;

  // Return the current segment's checkpoint level.
  for (const configuredCheckpoint of dreamDriftGetCheckpoints()) {
    if (node < configuredCheckpoint) return checkpoint;
    checkpoint = configuredCheckpoint;
  }

  // Nodes after the final checkpoint resume from that checkpoint.
  return checkpoint;
}

/**
 * Check whether the active instance can select a hero for its next level.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether an idle ongoing instance has another level to prepare.
 */
export function dreamDriftCanSelectHero(state: State, now: number): boolean {
  // Hero selection is only meaningful during an ongoing instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.result) return false;

  // Dream Drift cannot prepare while another Match-2 puzzle is active.
  if (state.match2.playing) return false;

  // An instance at its deadline cannot prepare another level attempt.
  if (dreamDriftGetInstanceRemainingTime(state, now) === 0) return false;

  // Only one Dream Drift level attempt may be active at a time.
  if (instance.playing !== 0) return false;

  // A next node must remain on the configured path.
  if (instance.progress >= dreamDriftGetFinalNode()) return false;

  // All pre-level selection gates passed.
  return true;
}

/**
 * Check whether the active instance can start its next Dream Drift level.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative current timestamp.
 * @returns Whether a valid hero and next node are ready with no active attempt.
 */
export function dreamDriftCanStartLevel(state: State, now: number): boolean {
  // A level can start only inside an ongoing instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.result) return false;

  // Dream Drift cannot start while another Match-2 puzzle is active.
  if (state.match2.playing) return false;

  // An instance at its deadline cannot start another level attempt.
  if (dreamDriftGetInstanceRemainingTime(state, now) === 0) return false;

  // Only one Dream Drift level attempt may be active at a time.
  if (instance.playing !== 0) return false;

  // A valid hero must be selected for the next level attempt.
  const heroIsConfigured = dreamDriftHeroes.some(
    (configuredHero) => configuredHero === instance.hero,
  );
  if (!heroIsConfigured) return false;

  // A next node must remain on the configured path.
  if (instance.progress >= dreamDriftGetFinalNode()) return false;

  // All level-start gates passed.
  return true;
}

/**
 * Build the deterministic level rotation for the configured progression path.
 *
 * @param state Player state containing the instance seed.
 * @returns Source Match-2 level for each configured node, or an empty list.
 */
export function dreamDriftGetLevelRotation(state: State): number[] {
  // A stored instance is required to seed its stable level rotation.
  const instance = state.dreamDrift?.instance;
  if (!instance) return [];

  // Seed every difficulty band from the same stable instance identity.
  const random = createPrng(
    `dream-drift-levels-v1-${state.id}-${instance.startedAt}`,
  );

  // Shuffle copies so the shared ruleset level pools remain immutable.
  const shuffledLevels = {
    easy: [...dreamDriftLevels.easy],
    medium: [...dreamDriftLevels.medium],
    hard: [...dreamDriftLevels.hard],
  };
  shuffleArray(shuffledLevels.easy, random);
  shuffleArray(shuffledLevels.medium, random);
  shuffleArray(shuffledLevels.hard, random);

  // Draw each node from its configured band without reusing a source level.
  const drawnCounts = { easy: 0, medium: 0, hard: 0 };
  return dreamDriftNodeBands.map(
    (band) => shuffledLevels[band][drawnCounts[band]++],
  );
}

/**
 * Get the source Match-2 level assigned to a Dream Drift progression node.
 *
 * @param state Player state containing the instance rotation seed.
 * @param node One-based Dream Drift progression node.
 * @returns Assigned Match-2 level, or undefined for an invalid node.
 */
export function dreamDriftGetPuzzleLevel(
  state: State,
  node: number,
): number | undefined {
  // Only configured one-based integer nodes can resolve a puzzle level.
  if (!Number.isInteger(node) || node < 1 || node > dreamDriftGetFinalNode()) {
    return undefined;
  }

  // Return the stable source level assigned to the requested node.
  return dreamDriftGetLevelRotation(state)[node - 1];
}
